// Angular
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';

// Angular Material
import { MatButtonModule } from '@angular/material/button';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatNativeDateModule } from '@angular/material/core';

// Third party
import { takeUntil } from 'rxjs';

// Local library
import {
  AccountFacade,
  IOverview,
  IOverviewBetRow,
  IOverviewTotalRow,
  OverviewService
} from '@amwager/account/data-access';
import {
  MatNotificationService,
  NumberWithCommasPipe,
  UnsubscribeObservableService,
  TranslatePipe
} from '@amwager/util';
import { ICustomerActivityOverviewModel } from '@amwager/util-ts';

/**
 * Account overview — date-range totals plus best/worst bet tables.
 * Ported from the legacy statements "Overview" tab; form + calculations
 * live in OverviewService. (Trend charts are intentionally omitted.)
 */
@Component({
  selector: 'feature-mat-account-overview',
  templateUrl: './overview.component.html',
  styleUrls: ['./overview.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatDatepickerModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatNativeDateModule,
    NumberWithCommasPipe,
    TranslatePipe
  ]
})
export class OverviewComponent
  extends UnsubscribeObservableService
  implements OnInit, OnDestroy {

  private readonly HOST = 'OverviewComponent';

  readonly maxDate = new Date();

  // Trend-chart panels shown as placeholders (data plotting not yet ported)
  readonly chartTitles = ['Wagered', 'Winnings', 'Reward', 'Win/Loss'];

  getOverviewForm!: FormGroup;
  totals: IOverviewTotalRow[] = [];
  bestBets: IOverviewBetRow[] = [];
  worstBets: IOverviewBetRow[] = [];

  constructor(
    private readonly overviewService: OverviewService,
    private readonly accountFacade: AccountFacade,
    private readonly matNotificationService: MatNotificationService,
    private readonly cdRef: ChangeDetectorRef
  ) {
    super();
  }

  ngOnInit(): void {
    this.getOverviewForm = this.overviewService.buildForm();

    // Initial fetch + subsequent results
    this.fetchOverview(`${this.HOST}:ngOnInit`);
    this.accountFacade.getOverview$()
      .pipe(takeUntil(this.toUnsubscribe$))
      .subscribe((overview?: IOverview) => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const response = overview?.response as any;
        const error = response?.error ?? response?.Error;
        if (error) {
          this.resetData();
          this.showAlert('Error', typeof error === 'string' ? error : error?.Message);
        } else {
          // Payload may be wrapped in Model
          const container = response?.Model ?? response;
          const overviewData: ICustomerActivityOverviewModel =
            container?.CustomerActivityOverview ?? {};
          this.totals = this.overviewService.buildTotals(overviewData.CustomerActivity ?? []);
          this.bestBets = this.overviewService.buildBets(overviewData.TopBets ?? [], true);
          this.worstBets = this.overviewService.buildBets(overviewData.WorstBets ?? [], false);
        }
        this.cdRef.markForCheck();
      });
  }

  override ngOnDestroy(): void {
    super.ngOnDestroy();
  }

  /**
   * Search button — validates the range then dispatches the fetch
   * @returns {void}
   */
  searchOverview(): void {
    if (this.overviewService.isDateRangeInvalid(this.getOverviewForm)) {
      this.showAlert('Error', 'Date To should not be less than Date From.');
      return;
    }
    this.fetchOverview(`${this.HOST}:searchOverview`);
  }

  /**
   * trackBy for the bet tables
   * @param {number} index Loop index
   * @param {IOverviewBetRow} row Bet row
   * @returns {string} Stable key
   */
  trackByBet(index: number, row: IOverviewBetRow): string {
    return `${row.eventName}-${row.poolId}-${index}`;
  }

  private fetchOverview(source: string): void {
    this.accountFacade.getOverview(
      source,
      this.overviewService.buildRequest(this.getOverviewForm)
    );
  }

  private resetData(): void {
    this.totals = [];
    this.bestBets = [];
    this.worstBets = [];
  }

  private showAlert(header: string, message: string): void {
    this.matNotificationService.showAlert(header, message ?? '', '', '', 'Close');
  }
}
